import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Stack, Chip,
  TextField, Button, IconButton, Tooltip, Typography, Alert, InputAdornment,
  CircularProgress, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Paper, Switch,
} from '@mui/material';
import {
  Close as CloseIcon, Add as AddIcon, Delete as DeleteIcon,
  Save as SaveIcon, LocalOffer as PackageIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import swal from '../../utils/swal';

// PackagesDialog — CRUD for the tiered packages under one income type.
// Example: Transportation → In-and-Out / In-only / Out-only, each with
// its own default daily amount. Blank list ⇒ this income type has no
// tiers (the registration UI hides the Package column entirely).
export default function PackagesDialog({ open, incomeType, onClose }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newAmount, setNewAmount] = useState('');

  const load = async () => {
    if (!incomeType) return;
    setLoading(true);
    try {
      const r = await API.get(`/collections/income-types/${encodeURIComponent(incomeType)}/packages`);
      setPackages(r.data || []);
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not load packages'));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) { setNewLabel(''); setNewAmount(''); load(); }
    /* eslint-disable-next-line */ }, [open, incomeType]);

  const addPackage = async () => {
    const label = newLabel.trim();
    if (!label) return swal.error('Enter a label first', 'Missing label');
    setSaving(true);
    try {
      await API.post(`/collections/income-types/${encodeURIComponent(incomeType)}/packages`, {
        label,
        default_amount: Number(newAmount) || 0,
        sort_order: packages.length * 10 + 10,
      });
      setNewLabel(''); setNewAmount('');
      await load();
    } catch (e) { swal.error(swal.pickError(e, 'Could not add package')); }
    finally { setSaving(false); }
  };

  const updateField = async (row, field, value) => {
    try {
      await API.put(`/collections/income-types/packages/${row.id}`,
        { [field]: value });
      await load();
    } catch (e) { swal.error(swal.pickError(e, 'Update failed')); }
  };

  const removePackage = async (row) => {
    const ok = await swal.confirmDelete(`package "${row.label}"`);
    if (!ok) return;
    try {
      const r = await API.delete(`/collections/income-types/packages/${row.id}`);
      if (r.data?.message) swal.success(r.data.message);
      await load();
    } catch (e) { swal.error(swal.pickError(e, 'Delete failed')); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
        color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5, py: 2,
      }}>
        <PackageIcon />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
            Packages · {incomeType}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
            Tiered daily rates that all roll up under this income type
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: 'background.default' }}>
        <Alert severity="info" variant="outlined" sx={{ borderRadius: 1.5, mb: 2 }}>
          Add each tier once with its <b>default daily amount</b>. On the
          Collection Registration page, picking a tier auto-fills the amount
          for that student — the admin can still override per row.
        </Alert>

        {/* Add row */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mb: 2 }}>
          <TextField size="small" label="Package label"
            placeholder="e.g. In & Out"
            value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
            sx={{ flex: 2 }} />
          <TextField size="small" label="Default amount"
            type="number" value={newAmount}
            onChange={(e) => setNewAmount(e.target.value)}
            inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
            InputProps={{ startAdornment: <InputAdornment position="start">GHS</InputAdornment> }}
            sx={{ flex: 1 }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={addPackage}
            disabled={saving || !newLabel.trim()} sx={{ minWidth: 120 }}>
            Add
          </Button>
        </Stack>

        {/* Existing packages */}
        {loading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={22} /></Box>
        ) : packages.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
            <PackageIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 0.5 }} />
            <Typography variant="body2">
              No packages yet — add one above to get started.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
            <Table size="small" sx={{
              '& .MuiTableCell-head': {
                bgcolor: 'grey.50', fontSize: 11, fontWeight: 800,
                textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary',
              },
            }}>
              <TableHead>
                <TableRow>
                  <TableCell>Label</TableCell>
                  <TableCell align="right" sx={{ width: 160 }}>Default amount (GHS)</TableCell>
                  <TableCell align="center" sx={{ width: 90 }}>Active</TableCell>
                  <TableCell align="right" sx={{ width: 56 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {packages.map(p => (
                  <TableRow key={p.id} sx={{ opacity: p.active ? 1 : 0.55 }}>
                    <TableCell>
                      <TextField size="small" fullWidth
                        defaultValue={p.label}
                        key={`lbl-${p.id}-${p.label}`}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v && v !== p.label) updateField(p, 'label', v);
                        }} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField size="small" type="number"
                        defaultValue={Number(p.default_amount) || 0}
                        key={`amt-${p.id}-${p.default_amount}`}
                        onBlur={(e) => {
                          const v = Number(e.target.value) || 0;
                          if (v !== Number(p.default_amount)) updateField(p, 'default_amount', v);
                        }}
                        inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                        sx={{ width: 130 }} />
                    </TableCell>
                    <TableCell align="center">
                      <Switch size="small" checked={!!p.active}
                        onChange={() => updateField(p, 'active', p.active ? 0 : 1)} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Delete package">
                        <IconButton size="small" color="error" onClick={() => removePackage(p)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.default' }}>
        <Chip size="small" variant="outlined"
          label={`${packages.length} package${packages.length === 1 ? '' : 's'}`}
          sx={{ fontWeight: 700 }} />
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} variant="outlined">Close</Button>
      </DialogActions>
    </Dialog>
  );
}
